import { useEffect, useLayoutEffect, useRef } from 'react';
import { avancement, dismissTimer, formatRemaining, playAlertOnce, useTimers } from './timers';

/** Toutes les 1,5 s : assez rapproché pour qu'on l'entende, assez espacé pour ne pas saturer. */
const RING_INTERVAL_MS = 1_500;

export interface ActiveTimersProps {
  /**
   * Hauteur actuelle du bandeau (0 si non affiché), signalée à chaque
   * changement (audit UX/UI passe 2, B4 : le bandeau superposait le contenu
   * de la page sans que celle-ci ne réserve d'espace). `App.tsx` s'en sert
   * pour réserver dynamiquement cet espace en haut de la page plutôt que de
   * démonter le positionnement `fixed` — celui-ci reste nécessaire pour que
   * le bandeau reste visible par-dessus le mode cuisine (`CookMode.tsx`).
   */
  onHeightChange?: (height: number) => void;
}

/**
 * Minuteurs actifs, visibles depuis n'importe quel écran (US-09) : monté une
 * seule fois, au niveau de `App.tsx`, en position fixe avec un z-index
 * supérieur à celui du mode cuisine (`CookMode.tsx`, `z-50`). Pas de second
 * montage à l'intérieur de `CookMode` : la position `fixed` des deux éléments
 * les place tous deux par rapport à la fenêtre, l'ordre d'imbrication dans le
 * DOM n'a pas d'incidence sur l'empilement, seul le z-index compte.
 */
export function ActiveTimers({ onHeightChange }: ActiveTimersProps = {}): React.JSX.Element | null {
  const { timers, now } = useTimers();
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Sonnerie continue tant qu'au moins un minuteur est échu et toujours
  // affiché (pas encore acquitté via « OK ») : un unique bip à l'échéance
  // (timers.ts, fireAlert) peut passer inaperçu si on n'est pas devant
  // l'écran à la seconde près. Un seul minuteur de cuisine réel sonne
  // jusqu'à ce qu'on l'arrête ; ici un seul intervalle partagé pour tous les
  // minuteurs échus plutôt qu'un par minuteur, pour ne pas superposer
  // plusieurs sonneries si deux minuteurs se terminent en même temps.
  const isRinging = timers.some((timer) => timer.fired_at !== null);
  useEffect(() => {
    if (!isRinging) return;
    const interval = setInterval(playAlertOnce, RING_INTERVAL_MS);
    return () => { clearInterval(interval); };
  }, [isRinging]);

  // Signale la hauteur réelle du bandeau (0 quand il n'y a aucun minuteur) à
  // chaque fois que le nombre de minuteurs change — c'est ce qui fait varier
  // la hauteur, jamais le texte du temps restant (une seule ligne, taille
  // fixe). `useLayoutEffect` : mesuré avant la peinture du navigateur, pour
  // que l'espace réservé par `App.tsx` n'ait jamais de rendu intermédiaire décalé.
  useLayoutEffect(() => {
    const height = timers.length === 0 ? 0 : (containerRef.current?.offsetHeight ?? 0);
    onHeightChange?.(height);
    // Publiée aussi en variable CSS globale (US-17) : le mode cuisine est une
    // surcouche `fixed inset-0` montée ailleurs dans l'arbre, il ne peut pas
    // recevoir cette hauteur par props. Sans elle, le bandeau de minuteurs
    // recouvrait son en-tête — donc la sortie du mode cuisine et le réglage
    // de taille de texte devenaient inaccessibles dès qu'un minuteur tournait
    // (audit externe, constat 06).
    document.documentElement.style.setProperty('--mlc-timers-height', `${height}px`);
  }, [timers.length, onHeightChange]);

  if (timers.length === 0) return null;

  // Ombre du design system (B3/F3, refonte UX/UI passe 3) plutôt que le
  // `shadow-lg` Tailwind générique d'origine — seul token d'élévation
  // adapté à un bandeau qui se superpose au contenu de la page.
  return (
    <div
      ref={containerRef}
      role="status"
      aria-label="Minuteurs actifs"
      className="fixed inset-x-0 top-0 z-[60] flex flex-col gap-2 bg-secondary p-3 text-secondary-foreground shadow-overlay"
    >
      {timers.map((timer) => {
        const remaining = timer.end_at - now;
        const done = timer.fired_at !== null || remaining <= 0;
        const part = avancement(timer, now);
        return (
          <div
            key={timer.id}
            className={`flex flex-col gap-2 rounded-control px-2 py-1.5 transition-colors duration-200 ${done ? 'bg-warning-bg text-warning' : ''}`}
          >
            {/*
              **Le décompte d'abord, et il ne se comprime pas** (US-102).

              La rangée était `label · temps · bouton`, tous trois libres de
              s'étirer : sur un téléphone de 390 px, une étiquette d'étape de
              quarante caractères prenait deux lignes et poussait le décompte
              **hors de l'écran**. Mesuré, capture à l'appui — le minuteur
              affichait le nom de l'étape et « Arrêter », et pas l'heure.

              L'ordre suit l'importance : le temps restant est ce qu'on vient
              lire, il est donc en tête, en grand, et `shrink-0` ; c'est
              l'étiquette qui se tronque, sur une seule ligne, parce qu'elle
              n'est là que pour dire *lequel* des minuteurs sonne.
            */}
            <div className="flex items-center gap-3">
              <span
                className="text-data shrink-0 text-3xl leading-none"
                aria-label={done ? 'Minuteur terminé' : `Temps restant : ${formatRemaining(remaining)}`}
                aria-live={done ? 'assertive' : 'off'}
              >
                {done ? 'Terminé' : formatRemaining(remaining)}
              </span>
              <span className="min-w-0 flex-1 truncate text-sm">{timer.label}</span>
              <button
                type="button"
                onClick={() => { dismissTimer(timer.id); }}
                className={`flex min-h-touch shrink-0 items-center justify-center rounded-control border px-3 text-sm transition-colors ${done ? 'border-warning' : 'border-secondary-foreground/40'}`}
              >
                {done ? 'Effacer' : 'Arrêter'}
              </button>
            </div>
            {/*
              **La barre se lit sans se lire** (US-102). Un nombre qui
              décroît demande d'être lu, puis comparé à celui d'il y a dix
              secondes ; une barre dit « bientôt » ou « pas encore » d'un coup
              d'œil, depuis l'autre bout de la cuisine.

              Absente quand le départ n'est pas connu — un minuteur lancé
              avant cette story : une barre vide sur un minuteur à moitié
              écoulé serait un mensonge, une barre absente n'est qu'une
              absence.

              L'opacité est admise ici : le §4 la proscrit sur un **contrôle
              porteur de texte**, dont le contraste interne s'effondre. Ce
              rail n'en porte pas, et sa raison d'être est justement d'être
              en retrait de la part remplie.
            */}
            {part !== null && (
              <div
                className={`h-1.5 w-full overflow-hidden rounded-full ${done ? 'bg-warning/25' : 'bg-secondary-foreground/25'}`}
                role="presentation"
              >
                <div
                  className={`h-full rounded-full transition-[width] duration-1000 ease-linear ${done ? 'bg-warning' : 'bg-secondary-foreground'}`}
                  style={{ width: `${String(Math.round(part * 100))}%` }}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
